import { InputPersonName } from '@/components/Input/PersonName/InputPersonName';
import type {
  PersonNameChildAttribute,
  PersonNameValue,
} from '@/components/Input/PersonName/types';
import { usePersonName } from '@/components/Input/PersonName/usePersonName';
import { VStack } from '@/components/Layout';
import { Text } from '@/components/Typography/Text';

import {
  CFormItemBase,
  type CFormItemProps,
  buildCFormItem,
} from '../CFormItem';

type Props = CFormItemProps & {
  value?: PersonNameValue;
  onChange: (value: PersonNameValue) => void;
  /** 子属性（各フィールドのname/required/readonlyを個別に指定） */
  childAttributes?: PersonNameChildAttribute[];
};

const DEFAULT_VALUE: PersonNameValue = {
  familyName: '',
  givenName: '',
  middleName: '',
  familyNameKana: '',
  givenNameKana: '',
  middleNameKana: '',
  familyNameEn: '',
  givenNameEn: '',
  middleNameEn: '',
  previousFamilyName: '',
  previousFamilyNameKana: '',
  previousFamilyNameEn: '',
};

/**
 * Card-based Form / PersonNameItem
 *
 * 氏名入力用のCFormアイテム。
 * childAttributesに含まれるフィールドのみが表示される。
 * 表示順序: 姓→名→ミドルネーム→旧姓（名前・カナ・英語名の各行）
 */
export const CFormPersonNameItem = buildCFormItem((props: Props) => {
  const value = props.value ?? DEFAULT_VALUE;

  // 表示用のフォーマット済み値を取得
  const {
    displayName,
    displayNameKana,
    displayNameEn,
    displayPreviousName,
    displayPreviousNameKana,
    displayPreviousNameEn,
  } = usePersonName({
    value,
    onChange: props.onChange,
    childAttributes: props.childAttributes,
  });

  const hasPreviousName =
    displayPreviousName || displayPreviousNameKana || displayPreviousNameEn;

  // 読み取り専用時の表示
  const readComponent = (
    <VStack className="gap-1">
      <Text variant="labelLarge">{displayName}</Text>
      {displayNameKana && (
        <Text variant="pSmall" className="text-neutral-muted-on-fill">
          {displayNameKana}
        </Text>
      )}
      {displayNameEn && (
        <Text variant="pSmall" className="text-neutral-muted-on-fill">
          {displayNameEn}
        </Text>
      )}
      {hasPreviousName && (
        <Text variant="pSmall" className="text-neutral-muted-on-fill">
          {[displayPreviousName, displayPreviousNameKana, displayPreviousNameEn]
            .filter(Boolean)
            .join(' / ')}
        </Text>
      )}
    </VStack>
  );

  return (
    <CFormItemBase
      {...props}
      readComponent={readComponent}
      writeComponent={
        <InputPersonName
          value={value}
          onChange={props.onChange}
          childAttributes={props.childAttributes}
          readonly={props.readonly}
        />
      }
    />
  );
});
